import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, KeyRound, Lock, Loader2, Mail, ShieldCheck, User } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { validateEmail, validateCPF, validateCNPJ, maskCpfCnpj } from '@/lib/validators';
import { cn } from '@/lib/utils';

type Mode = 'signin' | 'signup' | 'magic' | 'recovery';

const TERMS_VERSION = '1.1';

// ─── Campo de senha com toggle de visibilidade ────────────────────────────────

function PasswordInput({
  id,
  value,
  onChange,
  placeholder = 'Senha',
  autoComplete = 'current-password',
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
      <input
        id={id}
        type={show ? 'text' : 'password'}
        required
        minLength={8}
        autoComplete={autoComplete}
        placeholder={placeholder}
        className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-border bg-surface-raised text-sm focus:border-brand-500 outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink"
        tabIndex={-1}
        aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ─── Indicador de força de senha ──────────────────────────────────────────────

function PasswordStrength({ password }: { password: string }) {
  if (!password) return null;
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ['Muito fraca', 'Fraca', 'Razoável', 'Forte', 'Muito forte'];
  const colors = ['bg-negative', 'bg-negative', 'bg-warning', 'bg-positive', 'bg-positive'];

  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn('h-1 flex-1 rounded-full transition-colors', i < score ? colors[score] : 'bg-border')}
          />
        ))}
      </div>
      <p className={cn('text-[11px]', score <= 1 ? 'text-negative' : score <= 2 ? 'text-warning' : 'text-positive')}>
        {labels[score]}
        {score < 4 && (
          <span className="text-ink-soft ml-1">— use maiúsculas, números e símbolos</span>
        )}
      </p>
    </div>
  );
}

// ─── Tradutor de erros do Supabase ────────────────────────────────────────────

function translateError(msg: string): string {
  if (msg.includes('Invalid login credentials'))           return 'E-mail ou senha incorretos.';
  if (msg.includes('Email not confirmed'))                 return 'Confirme seu e-mail antes de continuar. Verifique sua caixa de entrada.';
  if (msg.includes('already registered') ||
      msg.includes('User already registered'))             return 'Este e-mail já está cadastrado. Use a opção "Já tenho conta".';
  if (msg.includes('rate limit') ||
      msg.includes('For security purposes'))               return 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';
  if (msg.includes('invalid format') ||
      msg.includes('Unable to validate email'))            return 'Formato de e-mail inválido.';
  if (msg.includes('Password should be'))                  return 'A senha deve ter no mínimo 8 caracteres.';
  if (msg.includes('Token has expired') ||
      msg.includes('token is invalid'))                    return 'O link expirou ou já foi usado. Solicite um novo.';
  if (msg.includes('over_email_send_rate_limit'))          return 'Muitos e-mails enviados. Aguarde alguns minutos.';
  return msg;
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function Login() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode]         = useState<Mode>('signin');
  const [recoveryMode, setRecoveryMode] = useState(false); // true quando usuário retorna do link de recuperação

  // Campos comuns
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [info, setInfo]             = useState<string | null>(null);

  // Campos do cadastro
  const [fullName, setFullName]               = useState('');
  const [documentNumber, setDocumentNumber]   = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted]     = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn]   = useState(false);

  // Campos de nova senha (recovery)
  const [newPassword, setNewPassword]               = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Validação de e-mail em tempo real
  const emailErr = email.length > 0 ? validateEmail(email) : null;

  // Cooldown para reenvio de e-mail (recovery + magic link): 60 segundos
  const [emailCooldown, setEmailCooldown] = useState(0);
  useEffect(() => {
    if (emailCooldown <= 0) return;
    const t = setTimeout(() => setEmailCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [emailCooldown]);

  // Detecta quando o usuário retorna de um link de recuperação de senha
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setRecoveryMode(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (authLoading) {
    return (
      <div className="h-full flex items-center justify-center gap-2 text-ink-muted">
        <Loader2 className="w-5 h-5 animate-spin" />
        Carregando…
      </div>
    );
  }

  // Usuário autenticado fora do modo recovery → vai direto para o app
  if (user && !recoveryMode) return <Navigate to="/farms" replace />;

  function resetForm() {
    setError(null);
    setInfo(null);
    setPassword('');
    setFullName('');
    setDocumentNumber('');
    setConfirmPassword('');
    setTermsAccepted(false);
    setPrivacyAccepted(false);
    setMarketingOptIn(false);
  }

  function switchMode(m: Mode) {
    setMode(m);
    resetForm();
  }

  // CPF (11 dígitos) ou CNPJ (14 dígitos) com dígito verificador válido
  const docDigits = documentNumber.replace(/\D/g, '');
  const documentValid =
    (docDigits.length === 11 && validateCPF(docDigits)) ||
    (docDigits.length === 14 && validateCNPJ(docDigits));
  const documentErr =
    documentNumber.length > 0 && !documentValid
      ? (docDigits.length < 11
          ? 'Digite um CPF (11 dígitos) ou CNPJ (14 dígitos).'
          : 'Documento inválido — confira os dígitos.')
      : null;

  const signupValid =
    fullName.trim().length >= 2 &&
    email.trim().length > 0 &&
    !emailErr &&
    documentValid &&
    password.length >= 8 &&
    password === confirmPassword &&
    termsAccepted &&
    privacyAccepted;

  const passwordMismatch    = confirmPassword.length > 0 && password !== confirmPassword;
  const newPasswordMismatch = confirmNewPassword.length > 0 && newPassword !== confirmNewPassword;

  // ── Redefinir senha (usuário retornou do link de recuperação) ─────────────────
  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) { setError('As senhas não coincidem.'); return; }
    if (newPassword.length < 8) { setError('A senha deve ter no mínimo 8 caracteres.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password: newPassword });
      if (updateErr) throw updateErr;
      setRecoveryMode(false);
      navigate('/farms', { replace: true });
    } catch (err) {
      setError(translateError((err as Error).message));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Submit principal ──────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (emailErr) { setError(emailErr); return; }

    setSubmitting(true);
    setError(null);
    setInfo(null);

    try {
      const now = new Date().toISOString();

      if (mode === 'signin') {
        const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
        if (signInErr) throw signInErr;

      } else if (mode === 'signup') {
        if (!signupValid) { setError('Preencha todos os campos obrigatórios e aceite os termos.'); return; }
        if (passwordMismatch) { setError('As senhas não coincidem.'); return; }

        const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin + '/onboarding',
            data: {
              full_name:           fullName.trim(),
              document_number:     docDigits,
              document_type:       docDigits.length === 14 ? 'CNPJ' : 'CPF',
              terms_accepted_at:   now,
              terms_version:       TERMS_VERSION,
              privacy_accepted_at: now,
              marketing_opt_in:    marketingOptIn,
            },
          },
        });
        if (signUpErr) throw signUpErr;
        // Com confirmação de e-mail ativa, o Supabase não retorna erro para
        // e-mail já cadastrado (anti-enumeração) — mas devolve identities vazio.
        if (signUpData.user && (signUpData.user.identities?.length ?? 0) === 0) {
          setError('Este e-mail já está cadastrado. Use "Já tenho conta" para entrar ou "Esqueci minha senha" para recuperar o acesso.');
          return;
        }
        setInfo('Verifique seu e-mail para confirmar a conta. Após a confirmação você será redirecionado para configurar sua empresa.');

      } else if (mode === 'recovery') {
        const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + '/login',
        });
        if (resetErr) throw resetErr;
        setInfo('Enviamos um link para redefinir sua senha. Verifique seu e-mail e clique no link.');
        setEmailCooldown(60);

      } else {
        // magic link
        const { error: otpErr } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: window.location.origin + '/farms' },
        });
        if (otpErr) throw otpErr;
        setInfo('Enviamos um link de acesso para o seu e-mail. O link é válido por 1 hora.');
        setEmailCooldown(60);
      }
    } catch (err) {
      setError(translateError((err as Error).message));
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Tela de redefinir senha (modo recovery ativo) ────────────────────────────
  if (recoveryMode) {
    return (
      <div className="min-h-full flex items-center justify-center bg-surface p-6">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-8">
            <Logo size={28} />
          </div>
          <div className="card p-8 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5 text-brand-700" />
              </div>
              <div>
                <h2 className="font-display text-lg font-bold text-ink">Criar nova senha</h2>
                <p className="text-xs text-ink-muted">Escolha uma senha forte para sua conta.</p>
              </div>
            </div>

            <form onSubmit={handleChangePassword} noValidate className="space-y-4">
              <div>
                <label htmlFor="new-pw" className="block text-xs font-medium text-ink-muted mb-1">
                  Nova senha <span className="text-negative">*</span>
                </label>
                <PasswordInput
                  id="new-pw"
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                />
                <PasswordStrength password={newPassword} />
              </div>
              <div>
                <label htmlFor="new-pw-confirm" className="block text-xs font-medium text-ink-muted mb-1">
                  Confirmar nova senha <span className="text-negative">*</span>
                </label>
                <PasswordInput
                  id="new-pw-confirm"
                  value={confirmNewPassword}
                  onChange={setConfirmNewPassword}
                  placeholder="Repita a nova senha"
                  autoComplete="new-password"
                />
                {newPasswordMismatch && (
                  <p className="text-xs text-negative mt-1">As senhas não coincidem.</p>
                )}
              </div>

              {error && (
                <div className="rounded-xl bg-negative/10 border border-negative/20 text-negative text-sm p-3">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || newPassword.length < 8 || newPassword !== confirmNewPassword}
                className="btn-primary w-full !py-2.5 gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar nova senha
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ─── Layout principal (dois painéis) ─────────────────────────────────────────
  return (
    <div className="min-h-full grid lg:grid-cols-2 bg-surface">

      {/* ── Painel de branding ───────────────────────────────────────── */}
      <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-b from-brand-950 via-brand-900 to-brand-700 text-ink-inverse">
        <Logo variant="inverse" size={32} />
        <div className="max-w-md">
          <h1 className="font-display text-4xl font-extrabold leading-tight">
            BPO Financeiro <span className="text-brand-300">+</span> gestão de e-commerce em uma única plataforma.
          </h1>
          <p className="mt-4 text-brand-100/80">
            Caixa, contas a pagar, DRE, conciliação bancária e o seu Mercado Livre — tudo em tempo real,
            com auditoria completa e segurança de fintech.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            {[
              'Multi-tenant com isolamento via RLS no Postgres',
              'Auditoria completa de toda operação financeira',
              'Tokens OAuth cifrados em repouso (AES-256-GCM)',
              'Conformidade LGPD — seus dados só seus',
            ].map((item) => (
              <li key={item} className="flex gap-2">
                <ShieldCheck className="w-5 h-5 text-brand-300 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-brand-200/70">© MYND · {new Date().getFullYear()}</p>
      </div>

      {/* ── Formulário ───────────────────────────────────────────────── */}
      <div className="flex items-start justify-center p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md py-4">

          <div className="lg:hidden mb-8">
            <Logo size={28} />
          </div>

          {/* Título */}
          <h2 className="font-display text-2xl font-bold">
            {mode === 'signup'   ? 'Criar conta gratuita'
             : mode === 'recovery' ? 'Recuperar acesso'
             : 'Acessar painel'}
          </h2>
          <p className="text-ink-muted text-sm mt-1 mb-6">
            {mode === 'signup'    ? 'Preencha os dados abaixo para começar.'
             : mode === 'recovery' ? 'Informe seu e-mail para receber o link de redefinição.'
             : mode === 'magic'   ? 'Enviaremos um link de acesso direto para o seu e-mail.'
             : 'Use o e-mail vinculado à sua conta.'}
          </p>

          {/* Botão voltar — modo recovery */}
          {mode === 'recovery' && (
            <button
              onClick={() => switchMode('signin')}
              className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o login
            </button>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">

            {/* Nome completo — só no cadastro */}
            {mode === 'signup' && (
              <div>
                <label htmlFor="fullname" className="block text-xs font-medium text-ink-muted mb-1">
                  Nome completo <span className="text-negative">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
                  <input
                    id="fullname"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Seu nome completo"
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-border bg-surface-raised text-sm focus:border-brand-500 outline-none"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* CPF/CNPJ — obrigatório no cadastro (segurança do marketplace) */}
            {mode === 'signup' && (
              <div>
                <label htmlFor="document" className="block text-xs font-medium text-ink-muted mb-1">
                  CPF ou CNPJ <span className="text-negative">*</span>
                </label>
                <input
                  id="document"
                  type="text"
                  required
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  maxLength={18}
                  className={`w-full px-3 py-2.5 rounded-xl border bg-surface-raised text-sm focus:border-brand-500 outline-none ${documentErr ? 'border-negative' : 'border-border'}`}
                  value={documentNumber}
                  onChange={(e) => setDocumentNumber(maskCpfCnpj(e.target.value))}
                />
                {documentErr && <p className="text-xs text-negative mt-1">{documentErr}</p>}
                {documentValid && <p className="text-xs text-positive mt-1">{docDigits.length === 14 ? 'CNPJ' : 'CPF'} válido ✓</p>}
              </div>
            )}

            {/* E-mail */}
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-ink-muted mb-1">
                E-mail <span className="text-negative">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="seu@email.com"
                  className={cn(
                    'w-full pl-9 pr-3 py-2.5 rounded-xl border bg-surface-raised text-sm focus:border-brand-500 outline-none',
                    emailErr ? 'border-negative' : 'border-border'
                  )}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {emailErr && <p className="text-xs text-negative mt-1">{emailErr}</p>}
            </div>

            {/* Senha — não no magic nem no recovery */}
            {mode !== 'magic' && mode !== 'recovery' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-xs font-medium text-ink-muted">
                    Senha <span className="text-negative">*</span>
                  </label>
                  {mode === 'signin' && (
                    <button
                      type="button"
                      onClick={() => switchMode('recovery')}
                      className="text-[11px] text-brand-700 hover:underline"
                    >
                      Esqueci minha senha
                    </button>
                  )}
                </div>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={setPassword}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                />
                {mode === 'signup' && <PasswordStrength password={password} />}
              </div>
            )}

            {/* Confirmação de senha — só no cadastro */}
            {mode === 'signup' && (
              <div>
                <label htmlFor="confirm" className="block text-xs font-medium text-ink-muted mb-1">
                  Confirmar senha <span className="text-negative">*</span>
                </label>
                <PasswordInput
                  id="confirm"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="Repita a senha"
                  autoComplete="new-password"
                />
                {passwordMismatch && (
                  <p className="text-xs text-negative mt-1">As senhas não coincidem.</p>
                )}
              </div>
            )}

            {/* Bloco de consentimento — só no cadastro */}
            {mode === 'signup' && (
              <div className="pt-2 space-y-3">
                <div className="border-t border-border pt-4">
                  <p className="text-xs font-semibold text-ink mb-3 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-brand-700" />
                    Termos e privacidade
                  </p>

                  <label className={cn(
                    'flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors mb-2',
                    termsAccepted ? 'border-brand-400 bg-brand-50/50' : 'border-border hover:border-brand-200'
                  )}>
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded shrink-0 accent-brand-700"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span className="text-xs text-ink leading-relaxed">
                      Li e aceito os{' '}
                      <Link to="/termos" target="_blank" className="text-brand-700 underline font-medium">
                        Termos de Uso e Serviço
                      </Link>.{' '}
                      <span className="text-negative font-medium">Obrigatório</span>
                    </span>
                  </label>

                  <label className={cn(
                    'flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors mb-2',
                    privacyAccepted ? 'border-brand-400 bg-brand-50/50' : 'border-border hover:border-brand-200'
                  )}>
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded shrink-0 accent-brand-700"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    />
                    <span className="text-xs text-ink leading-relaxed">
                      Li e concordo com a{' '}
                      <Link to="/privacidade" target="_blank" className="text-brand-700 underline font-medium">
                        Política de Privacidade
                      </Link>{' '}
                      e autorizo o tratamento dos meus dados conforme a LGPD.{' '}
                      <span className="text-negative font-medium">Obrigatório</span>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 p-3 rounded-xl border border-border hover:bg-surface-subtle/50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded shrink-0 accent-brand-700"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                    />
                    <span className="text-xs text-ink-muted leading-relaxed">
                      Quero receber novidades e dicas sobre a plataforma por e-mail.{' '}
                      <span className="text-ink-soft">Opcional</span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Feedback */}
            {error && (
              <div role="alert" className="rounded-xl bg-negative/10 border border-negative/20 text-negative text-sm p-3">
                {error}
              </div>
            )}
            {info && (
              <div role="status" className="rounded-xl bg-positive/10 border border-positive/20 text-positive text-sm p-3">
                {info}
              </div>
            )}

            {/* Botão principal — oculto em recovery/magic após envio bem-sucedido */}
            {!((mode === 'recovery' || mode === 'magic') && info) && (
              <button
                type="submit"
                disabled={
                  submitting ||
                  (mode === 'signup' && !signupValid) ||
                  Boolean(emailErr && email.length > 0) ||
                  ((mode === 'recovery' || mode === 'magic') && emailCooldown > 0)
                }
                className="btn-primary w-full !py-2.5 mt-2 gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting
                  ? 'Aguarde…'
                  : (mode === 'recovery' || mode === 'magic') && emailCooldown > 0
                  ? `Reenviar em ${emailCooldown}s`
                  : mode === 'signup'   ? 'Criar conta'
                  : mode === 'recovery' ? 'Enviar link de recuperação'
                  : mode === 'magic'    ? 'Enviar link de acesso'
                  : 'Entrar'}
              </button>
            )}
          </form>

          {/* Links de navegação entre modos */}
          {mode !== 'recovery' && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-sm">
              {mode !== 'signin' && (
                <button onClick={() => switchMode('signin')} className="text-brand-700 hover:underline">
                  Já tenho conta — Entrar
                </button>
              )}
              {mode !== 'signup' && (
                <button onClick={() => switchMode('signup')} className="text-brand-700 hover:underline">
                  Criar conta gratuita
                </button>
              )}
              {mode !== 'magic' && (
                <button onClick={() => switchMode('magic')} className="text-ink-muted hover:text-ink hover:underline text-xs">
                  Entrar por link mágico
                </button>
              )}
            </div>
          )}

          {/* Rodapé legal */}
          <p className="text-center text-[11px] text-ink-soft mt-8 leading-relaxed">
            Ao usar a plataforma você concorda com nossos{' '}
            <Link to="/termos" target="_blank" className="text-brand-700 hover:underline">Termos de Uso</Link>
            {' '}e{' '}
            <Link to="/privacidade" target="_blank" className="text-brand-700 underline">Política de Privacidade</Link>.
            <br />Dados protegidos conforme LGPD · © MYND {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
